import { useMemo } from 'react'
import { Text, View } from 'react-native'
import type { ImageSourcePropType } from 'react-native'
import { useTranslation } from 'react-i18next'
import { SelectField } from '../../shared/SelectField'
import type { SelectFieldOption } from '../../shared/SelectField'
import type { RewardItem } from '../shared/rewardItems'

interface BirthdayRewardActiveItemSectionProps {
  stepNumber: number
  rewardItems: RewardItem[]
  selectedRewardItemId: string | null
  onSelectedRewardItemIdChange: (id: string) => void
  disabled: boolean
  error?: string
}

export function BirthdayRewardActiveItemSection({
  stepNumber,
  rewardItems,
  selectedRewardItemId,
  onSelectedRewardItemIdChange,
  disabled,
  error,
}: BirthdayRewardActiveItemSectionProps) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language === 'sv' ? 'sv' : 'en'

  const options = useMemo<SelectFieldOption[]>(
    () =>
      rewardItems
        .filter((item) => item.active)
        .map((item) => ({
          label: item.name[lang] || item.name.en,
          value: item.id,
          imageSource: item.imageUrl ? ({ uri: item.imageUrl } as ImageSourcePropType) : undefined,
        })),
    [rewardItems, lang],
  )

  return (
    <View className="gap-5 rounded-xl border border-border bg-canvas-card p-6">
      <View>
        <View className="flex-row items-baseline gap-2">
          <Text className="text-lg font-semibold text-primary">{stepNumber}.</Text>
          <Text className="text-lg font-semibold text-primary">{t('birthdayReward.activeItem.sectionTitle')}</Text>
        </View>
        <Text className="mt-1 text-sm text-secondary">{t('birthdayReward.activeItem.subtitle')}</Text>
      </View>

      <View className={disabled ? 'opacity-50' : undefined} pointerEvents={disabled ? 'none' : 'auto'}>
        <SelectField
          value={selectedRewardItemId ?? ''}
          options={options}
          placeholder={t('birthdayReward.activeItem.placeholder')}
          emptyText={t('birthdayReward.activeItem.noRewardItems')}
          accessibilityLabel={t('birthdayReward.activeItem.sectionTitle')}
          error={error}
          onValueChange={onSelectedRewardItemIdChange}
        />
      </View>
    </View>
  )
}
